import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import type { MarketerDetail } from "../lib/types";
import {
  Badge,
  Card,
  CommissionStatusBadge,
  DetailRow,
  EmptyState,
  ErrorNote,
  Figure,
  OrderStatusBadge,
  PageHeader,
  Table,
  TableWrap,
  Td,
  Th,
  formatDate,
  formatMoney,
  statusLabel,
} from "../components/ui";

/**
 * One marketer: what they have sold, and what we owe them for it.
 *
 * The list screen can only show a count, which answers "have they sold
 * anything" and nothing else. Every question that follows — which products,
 * which customers, how much of the commission is still unpaid — needs the rows,
 * so they live here rather than being reconstructed from the orders screen with
 * a filter the admin has to remember to set.
 */
export function MarketerDetailRoute() {
  const { id = "" } = useParams();

  const detail = useQuery({
    queryKey: ["marketer", id],
    queryFn: () => api<MarketerDetail>(`/api/v1/admin/marketers/${id}`),
  });

  if (detail.isPending) return <p className="text-sm text-dim">Loading…</p>;
  if (detail.isError) {
    const missing = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <div className="space-y-4">
        <ErrorNote>{missing ? "No such marketer." : "Could not load this marketer."}</ErrorNote>
        <Link to="/marketers" className="text-sm text-accent underline underline-offset-4">
          Back to marketers
        </Link>
      </div>
    );
  }

  const { marketer, sales, totals } = detail.data;
  const account = marketer.users?.[0];

  // Unpaid is the number an admin is usually here for — what is owed but not
  // yet settled. Paid and void are history and are shown, but not led with.
  const owed = totals.commission.filter((row) => row.status === "pending" || row.status === "approved");

  return (
    <div className="space-y-6">
      <PageHeader
        title={marketer.name}
        lede={marketer.email}
        action={
          <Link to="/marketers" className="text-sm text-accent underline underline-offset-4">
            Back to marketers
          </Link>
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card title="Marketer">
          <div className="divide-y divide-hairline">
            <DetailRow label="Status">
              <Badge tone={marketer.active ? "live" : "quiet"}>
                {marketer.active ? "Active" : "Inactive"}
              </Badge>
            </DetailRow>
            <DetailRow label="Email">{marketer.email}</DetailRow>
            <DetailRow label="Phone">{marketer.phone ?? "—"}</DetailRow>
            <DetailRow label="Notes">{marketer.notes ?? "—"}</DetailRow>
            <DetailRow label="Added">{formatDate(marketer.createdAt)}</DetailRow>
            <DetailRow label="Portal access">
              {account === undefined
                ? "No sign-in"
                : account.disabledAt !== null
                  ? "Sign-in disabled"
                  : account.lastLoginAt === null
                    ? "Never signed in"
                    : `Last in ${formatDate(account.lastLoginAt)}`}
            </DetailRow>
          </div>
        </Card>

        <Card title="Totals">
          <div className="space-y-4 p-4">
            <dl className="grid grid-cols-2 gap-4">
              <Figure label="Sales" value={totals.orders} />
              <Figure label="Paid" value={totals.paidOrders} />
            </dl>

            {/* One block per currency. Summing across them would be wrong. */}
            {totals.revenue.length === 0 ? (
              <p className="text-sm text-dim">No paid sales yet.</p>
            ) : (
              <div className="space-y-2">
                {totals.revenue.map((row) => (
                  <div key={row.currency} className="flex items-baseline justify-between">
                    <span className="text-sm text-muted">
                      Revenue ({row.currency.toUpperCase()})
                    </span>
                    <span className="tabular text-sm text-ink">
                      {formatMoney(row.grossCents, row.currency)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2 border-t border-hairline pt-4">
              {totals.commission.length === 0 ? (
                <p className="text-sm text-dim">No commission recorded.</p>
              ) : (
                totals.commission.map((row) => (
                  <div
                    key={`${row.status}-${row.currency}`}
                    className="flex items-baseline justify-between"
                  >
                    <span className="text-sm text-muted">
                      {statusLabel(row.status)} ({row.count})
                    </span>
                    <span className="tabular text-sm text-ink">
                      {formatMoney(row.amountCents, row.currency)}
                    </span>
                  </div>
                ))
              )}
              {owed.length > 0 && (
                <p className="pt-1 text-xs text-dim">
                  Pending and approved are what is still owed. Settle them on the Commission
                  screen.
                </p>
              )}
            </div>
          </div>
        </Card>
      </div>

      <Card title="Sales">
        {sales.length === 0 ? (
          <EmptyState>
            Nothing attributed yet. A sale reaches this list when an order records this marketer —
            attribution is set when the order is created, never inferred afterwards.
          </EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Order</Th>
                  <Th>Customer</Th>
                  <Th>What sold</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Value</Th>
                  <Th className="text-right">Commission</Th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <tr key={sale.id} className="border-b border-hairline last:border-0">
                    <Td label="Order">
                      <Link
                        to={`/orders/${sale.id}`}
                        className="text-accent underline underline-offset-4"
                      >
                        {sale.number}
                      </Link>
                      <span className="block text-xs text-dim">
                        {sale.paidAt === null
                          ? `Created ${formatDate(sale.createdAt)}`
                          : `Paid ${formatDate(sale.paidAt)}`}
                      </span>
                    </Td>
                    <Td label="Customer" className="text-muted">
                      {sale.customer.name}
                    </Td>
                    <Td label="What sold">
                      <span className="block">{sale.product.name}</span>
                      <span className="block text-xs text-dim">
                        {sale.plan.name}
                        {sale.interval === null ? "" : ` · ${sale.interval}`}
                      </span>
                    </Td>
                    <Td label="Status">
                      <OrderStatusBadge status={sale.status} />
                    </Td>
                    <Td label="Value" className="tabular text-right">
                      {formatMoney(sale.oneTimeCents, sale.currency)}
                    </Td>
                    <Td label="Commission" className="tabular text-right">
                      {/* The order carries the snapshot; the ledger entry is
                          what we have committed to paying. They agree until a
                          refund voids one of them, and then the difference is
                          the thing worth seeing. */}
                      <span className="block">
                        {formatMoney(sale.commissionCents, sale.currency)}
                      </span>
                      {sale.commission !== null && (
                        <span className="mt-1 block">
                          <CommissionStatusBadge status={sale.commission.status} />
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}
